import { Alert, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, List, ListItem, ListItemText, Stack, TextField, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, errorMessage } from "../api/client";
import { PageHeader } from "../components";
import { formatDateTime, formatRelative } from "../i18n/format";
import help from "./HELP.md?raw";
import { useAuthEvents, useMfaStatus, useSessions } from "./useData";

function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <Card><CardContent>
      <Typography variant="subtitle2" gutterBottom>Change password</Typography>
      <Stack spacing={2} component="form" onSubmit={async (e) => {
        e.preventDefault();
        const { error } = await api.POST("/api/v1/accounts/auth/password/change/", { body: { current_password: current, new_password: next } });
        setMsg(error ? { ok: false, text: errorMessage(error) } : { ok: true, text: "Password updated. Other sessions were signed out." });
        if (!error) { setCurrent(""); setNext(""); }
      }}>
        {msg && <Alert severity={msg.ok ? "success" : "error"}>{msg.text}</Alert>}
        <TextField label="Current password" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
        <TextField label="New password" type="password" value={next} onChange={(e) => setNext(e.target.value)} required autoComplete="new-password" />
        <Button type="submit" variant="contained" sx={{ alignSelf: "flex-start" }}>Update password</Button>
      </Stack>
    </CardContent></Card>
  );
}

function Mfa() {
  const qc = useQueryClient();
  const status = useMfaStatus();
  const [setup, setSetup] = useState<{ secret: string; otpauth_url: string } | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [disabling, setDisabling] = useState(false);
  const done = () => void qc.invalidateQueries({ queryKey: ["mfa"] });
  return (
    <Card><CardContent>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Two-factor authentication</Typography>
        {status.data?.enabled ? <Chip label="Enabled" color="success" size="small" /> : <Chip label="Off" size="small" />}
        {status.data?.required && <Chip label="Required by your organization" color="warning" size="small" />}
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {recovery && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setRecovery(null)}>
          Save these recovery codes somewhere safe. Each works once.
          <pre style={{ margin: "8px 0 0" }}>{recovery.join("\n")}</pre>
        </Alert>
      )}
      {!status.data?.enabled && !setup && (
        <Button variant="contained" onClick={async () => { const { data } = await api.POST("/api/v1/accounts/auth/mfa/"); setSetup(data ?? null); }}>Set up authenticator app</Button>
      )}
      {setup && (
        <Stack spacing={2}>
          <Typography variant="body2">Scan this in Google Authenticator, 1Password or Authy, or enter the key manually: <code>{setup.secret}</code></Typography>
          <img alt="MFA QR code" width={180} height={180}
            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(setup.otpauth_url)}`} />
          <Stack direction="row" spacing={1}>
            <TextField label="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} inputProps={{ inputMode: "numeric" }} />
            <Button variant="contained" onClick={async () => {
              setError(null);
              const { data, error: err } = await api.POST("/api/v1/accounts/auth/mfa/confirm/", { body: { code } });
              if (err) { setError(errorMessage(err)); return; }
              setRecovery(data?.recovery_codes ?? []); setSetup(null); setCode(""); done();
            }}>Confirm</Button>
            <Button onClick={() => setSetup(null)}>Cancel</Button>
          </Stack>
        </Stack>
      )}
      {status.data?.enabled && (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{status.data.recovery_codes_remaining} recovery codes left.</Typography>
          <Button onClick={() => setDisabling(true)} color="error" disabled={status.data.required}>Disable</Button>
        </Stack>
      )}
      <Dialog open={disabling} onClose={() => setDisabling(false)}>
        <DialogTitle>Disable two-factor authentication</DialogTitle>
        <DialogContent><TextField label="Your password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mt: 1 }} autoFocus /></DialogContent>
        <DialogActions>
          <Button onClick={() => setDisabling(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={async () => {
            const { error: err } = await api.DELETE("/api/v1/accounts/auth/mfa/", { body: { password } as never });
            if (err) setError(errorMessage(err)); else { setDisabling(false); setPassword(""); done(); }
          }}>Disable</Button>
        </DialogActions>
      </Dialog>
    </CardContent></Card>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const sessions = useSessions();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["sessions"] });
  return (
    <Card><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Active sessions</Typography>
        <Button size="small" onClick={async () => { await api.DELETE("/api/v1/accounts/auth/sessions/"); refresh(); }}>Sign out other sessions</Button>
      </Stack>
      <List dense>
        {sessions.data?.map((s) => (
          <ListItem key={s.id} secondaryAction={!s.is_current && <Button size="small" onClick={async () => { await api.DELETE("/api/v1/accounts/auth/sessions/{id}/", { params: { path: { id: s.id } } }); refresh(); }}>Revoke</Button>}>
            <ListItemText primary={`${s.ip_address ?? "unknown IP"} ${s.is_current ? "(this device)" : ""}`} secondary={`${s.user_agent.slice(0, 80)} · last seen ${formatRelative(s.last_seen_at)}`} />
          </ListItem>
        ))}
      </List>
    </CardContent></Card>
  );
}

function RecentActivity() {
  const events = useAuthEvents();
  return (
    <Card><CardContent>
      <Typography variant="subtitle2" gutterBottom>Recent security activity</Typography>
      <List dense>
        {events.data?.slice(0, 15).map((e, i) => (
          <ListItem key={i}><ListItemText primary={e.kind.replace(/_/g, " ")} secondary={`${formatDateTime(e.created_at)} · ${e.ip_address ?? ""} ${e.detail}`} /></ListItem>
        ))}
      </List>
    </CardContent></Card>
  );
}

export function Security() {
  return (
    <>
      <PageHeader help={help} title="Security" crumbs={[{ label: "Settings" }, { label: "Security" }]} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}><Stack spacing={2}><ChangePassword /><Mfa /></Stack></Grid>
        <Grid size={{ xs: 12, md: 6 }}><Stack spacing={2}><Sessions /><RecentActivity /></Stack></Grid>
      </Grid>
    </>
  );
}
